import { autoinject } from 'aurelia-framework';
import { DialogService } from 'aurelia-dialog';
import { NetsSalesDashboardService } from 'services/statistics-dashboard-service';
import { FilterManager } from 'lib/tables/FilterManager';
import { Filters, filterTypes } from 'elements/Filter';
import { I18N } from 'aurelia-i18n';
import { TabManager } from 'views/admin/mooring/tab-manager';
import { PubSub } from 'lib/event/PubSub';
import { DecimalsValueConverter } from 'value-converters/decimals';
import { AsyncErrorHandler } from 'lib/ui';
import { QueryFilter } from 'lib/tables/DataFilter';
import { StatisticsDashboardCommonService } from 'services/statistics-dashboard-service';
import { StatisticsExportDialog, ExportDialogModel } from 'elements/statistics-export-dialog';
import { TableData, ChartData } from 'services/statistics-export-service';
import { NetsSalesDashboard, NetsSalesHistoryDto } from 'models/statistics/nets';

@autoinject()
export class TabNetsSales {
  protected queryFilter: QueryFilter = null;
  protected fetchingData: boolean = false;

  protected numberOfOffersInProgress: number = null;
  protected numberOfNetOffersTransferredToOrder: number = null;
  protected numberOfNetOffersSignedContracts: number = null;
  protected numberOfNetOffersLostOrExpired: number = null;
  protected offersToOrdersPercentage: string = null;
  protected netsSalesHistoryTitle: string = `${this.t.tr('general.last')} ${this.t.tr('general.sales')} Per ${this.t.tr('general.customer')}`;

  constructor(
    private netsSalesDashboardService: NetsSalesDashboardService,
    private pubsub: PubSub,
    private t: I18N,
    protected fm: FilterManager,
    private decimalsValueConverter: DecimalsValueConverter,
    tabManager: TabManager,
    private statisticsDashboardCommonService: StatisticsDashboardCommonService,
    private dialogService: DialogService
  ) {
    tabManager.tabActivated();
    this.pubsub.sub('filter-manager:filter-changed', () => {
      void this.getNetsSales();
    });
    this.pubsub.sub('i18n:locale:changed', () => this.onLocaleChanged());
  }

  onLocaleChanged() {
    this.netsSalesHistoryTitle = `${this.t.tr('general.last')} ${this.t.tr('general.sales')} Per ${this.t.tr('general.customer')}`;
  }

  attached() {
    const now = new Date();
    const year = now.getFullYear();
    const firstDay = new Date(year, 0, 1, 0, 0, 0, 0);
    const lastDay = new Date(year, 11, 31, 23, 59, 59, 999);

    this.statisticsDashboardCommonService.updateDatesInLocalStorage(firstDay, lastDay, 'statistics_nets_sales');

    this.fm.init({
      context: 'statistics_nets_sales',
      storageKey: 'statistics_nets_sales',
      filters: {
        dateOfGoodsReceipt: {
          name: 'dateOffset',
          label: this.t.tr('general.date'),
          type: filterTypes.RANGE_DATE,
          values: { from: new Date(), to: new Date() },
          defaultValues: { from: firstDay, to: lastDay },
        },
      } satisfies Filters,
      defaultFilters: {
        dateOfGoodsReceipt: true,
      },
    });

    this.queryFilter = this.fm.queryFilter;
  }

  detached() {
    this.pubsub.unsub();
  }

  @AsyncErrorHandler
  protected async getNetsSales() {
    this.queryFilter = this.fm.queryFilter;
    this.fetchingData = true;
    const res = await this.netsSalesDashboardService.getNetOffersCounts(this.fm.queryFilter);
    if (res) {
      this.numberOfOffersInProgress = res.data?.NumberOfNetOffersInProgress;
      this.numberOfNetOffersTransferredToOrder = res.data?.NumberOfNetOffersTransferredToOrder;
      this.numberOfNetOffersSignedContracts = res.data?.NumberOfNetOffersSignedContracts;
      this.numberOfNetOffersLostOrExpired = res.data?.NumberOfNetOffersLostOrExpired;
      const temp = (this.numberOfNetOffersTransferredToOrder / res.data?.TotalNumberOfOffers) * 100;
      this.offersToOrdersPercentage = this.decimalsValueConverter.toView(temp, 2);
    }
    this.fetchingData = false;
  }

  protected async openExportDialog(): Promise<void> {
    // Fetch all data needed for export (lazy load)
    const cutOffYears = 2;
    const { data: salesData } = await this.netsSalesDashboardService.getNetsSales(this.fm.queryFilter, cutOffYears);
    const { data: historyData } = await this.netsSalesDashboardService.getNetsSalesHistory({
      searchText: null,
      orderBy: 'LastSaleDate',
      top: 100,
      skip: 0,
      count: false,
      orderByDirection: 'DESC',
    });

    const tables = this.buildExportTables(historyData);
    const charts = this.buildExportCharts(salesData);
    const dateRange = this.getDateRange();

    const model: ExportDialogModel = {
      tables,
      charts,
      dateRange,
      reportTitle: 'Salg Statistikk',
    };

    this.dialogService.open({
      viewModel: StatisticsExportDialog,
      model,
      lock: false,
    });
  }

  private buildExportTables(historyData: NetsSalesHistoryDto[]): TableData[] {
    const tables: TableData[] = [];

    // 1. Summary table
    const summaryTable: TableData = {
      title: String(this.t.tr('general.summary')),
      headers: ['', String(this.t.tr('general.amount'))],
      rows: [
        [String(this.t.tr('netOffer.inProgress')), String(this.numberOfOffersInProgress ?? '--')],
        [String(this.t.tr('netOffer.offerTransferredToOrder')), String(this.numberOfNetOffersTransferredToOrder ?? '--')],
        [String(this.t.tr('netOffer.lostOrExpired')), String(this.numberOfNetOffersLostOrExpired ?? '--')],
        [String(this.t.tr('netOffer.offerToOrder')), `${this.offersToOrdersPercentage ?? '--'} %`],
      ],
    };
    tables.push(summaryTable);

    // 2. Sales History table
    if (historyData && historyData.length > 0) {
      const historyTable: TableData = {
        title: this.netsSalesHistoryTitle,
        headers: [
          String(this.t.tr('net.customer')),
          `${this.t.tr('general.last')} ${this.t.tr('general.sales')} ${this.t.tr('general.date')}`,
          `${this.t.tr('net.averageIntervalBetweenSales')} (${this.t.tr('general.days')})`,
          String(this.t.tr('general.averageNetsInOrder')),
          String(this.t.tr('general.totalSales')),
          String(this.t.tr('general.numberOfOrders')),
        ],
        rows: historyData.map((item) => [
          item.CustomerName ?? '--',
          item.LastSaleDate ? new Date(item.LastSaleDate).toLocaleDateString('nb-NO') : '--',
          item.IntervalBetweenSales?.toFixed(0) ?? '--',
          item.AverageNetsInOrder?.toFixed(2) ?? '--',
          String(item.TotalSales ?? '--'),
          String(item.NumberOfOrders ?? '--'),
        ]),
      };
      tables.push(historyTable);
    }

    return tables;
  }

  private buildExportCharts(salesData: NetsSalesDashboard): ChartData[] {
    const charts: ChartData[] = [];

    if (!salesData) {
      return charts;
    }

    // 1. Sales per Customer chart
    if (salesData.YearlySalesReportForCustomer?.length > 0) {
      const customerChart = this.buildYearlySalesChart(
        salesData.YearlySalesReportForCustomer,
        `${this.t.tr('general.sales')} Per ${this.t.tr('net.customer')}`,
        'CustomerName'
      );
      if (customerChart) {
        charts.push(customerChart);
      }
    }

    // 2. Sales per Design chart
    if (salesData.YearlySalesReportForNetDesign?.length > 0) {
      const designChart = this.buildYearlySalesChart(
        salesData.YearlySalesReportForNetDesign,
        `${this.t.tr('general.sales')} Per ${this.t.tr('netsummary.DesignType')}`,
        'NetDesign'
      );
      if (designChart) {
        charts.push(designChart);
      }
    }

    return charts;
  }

  private buildYearlySalesChart(
    data: { CustomerName?: string; NetDesign?: string; YearlySales: { SalesYear: number; SalesCount: number }[] }[],
    title: string,
    nameKey: 'CustomerName' | 'NetDesign'
  ): ChartData | null {
    if (!data || data.length === 0) {
      return null;
    }

    const years = Array.from(new Set(data.flatMap((item) => item.YearlySales?.map((s) => s.SalesYear) || []))).sort();
    const categories = years.map(String);

    const series = data.map((item) => {
      const name = item[nameKey] ?? '--';
      const values = years.map((year) => {
        const yearData = item.YearlySales?.find((s) => s.SalesYear === year);
        return yearData?.SalesCount ?? 0;
      });
      return { name, values };
    });

    return { title, categories, series };
  }

  private getDateRange(): { from: Date; to: Date } | undefined {
    const dateFilter = (this.fm.filters as Record<string, { values?: { from?: Date; to?: Date } }>)?.dateOfGoodsReceipt;
    if (dateFilter?.values?.from && dateFilter?.values?.to) {
      return {
        from: new Date(dateFilter.values.from),
        to: new Date(dateFilter.values.to),
      };
    }
    return undefined;
  }
}
